/**
 * T타입 수동 채점 모달 컴포넌트
 * - 학생 답안 이미지 표시
 * - 정답 이미지 표시 (없으면 안내 메시지)
 * - 정답/오답 처리 버튼
 */

import React, { useState } from 'react';
import { Modal, Row, Col, Card, Button, Space, Typography, Result, message } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { gradeSubmission } from '../../services/gradingService';
import type { PendingGradingItem } from '../../types/grading';

const { Text, Title } = Typography;

/**
 * ManualGradingModal Props
 */
interface ManualGradingModalProps {
  /** 채점할 제출 정보 */
  item: PendingGradingItem | null;
  /** 모달 열림 여부 */
  visible: boolean;
  /** 모달 닫기 핸들러 */
  onClose: () => void;
  /** 채점 완료 콜백 (결과 새로고침용) */
  onGraded: () => void;
}

/**
 * ManualGradingModal 컴포넌트
 */
export const ManualGradingModal: React.FC<ManualGradingModalProps> = ({
  item,
  visible,
  onClose,
  onGraded,
}) => {
  const [loading, setLoading] = useState(false);
  const [studentImageError, setStudentImageError] = useState(false);
  const [answerImageError, setAnswerImageError] = useState(false);

  /**
   * 채점 처리
   */
  const handleGrade = async (isCorrect: boolean) => {
    if (!item) return;

    try {
      setLoading(true);

      const response = await gradeSubmission(item.submission_id, isCorrect);

      message.success(response.message);

      // 채점 완료 콜백 (결과 새로고침)
      onGraded();

      // 모달 닫기
      onClose();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '채점 처리 실패');
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  return (
    <Modal
      title={
        <Title level={4}>
          T타입 채점 - {item.student_name} - 페이지 {item.page} 문제 {item.problem_number}번
        </Title>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
    >
      {/* 이미지 비교 */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        {/* 학생 답안 */}
        <Col span={12}>
          <Card title="학생 답안" bordered>
            {item.answer_image_url && !studentImageError ? (
              <img
                src={item.answer_image_url}
                alt="학생 답안"
                style={{
                  width: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  border: '1px solid #d9d9d9',
                }}
                onError={() => setStudentImageError(true)}
              />
            ) : (
              <Result
                status="warning"
                title="답안 이미지 없음"
                subTitle={
                  studentImageError
                    ? '이미지를 불러올 수 없습니다'
                    : '학생이 답안을 제출하지 않았습니다'
                }
              />
            )}
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">시도: {item.attempt_number}차</Text>
              {studentImageError && item.answer_image_url && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                    URL: {item.answer_image_url}
                  </Text>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* 정답 이미지 */}
        <Col span={12}>
          <Card title="정답" bordered>
            {item.problem_answer_image_url && !answerImageError ? (
              <img
                src={item.problem_answer_image_url}
                alt="정답"
                style={{
                  width: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  border: '1px solid #d9d9d9',
                }}
                onError={() => setAnswerImageError(true)}
              />
            ) : (
              <Result
                icon={<InfoCircleOutlined />}
                title="정답 이미지 없음"
                subTitle={
                  answerImageError
                    ? '이미지를 불러올 수 없습니다'
                    : '강사님이 직접 평가해주세요'
                }
                style={{
                  padding: '48px 0',
                }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* 채점 버튼 */}
      <div style={{ textAlign: 'center' }}>
        <Space size="large">
          <Button
            type="primary"
            size="large"
            icon={<CheckOutlined />}
            loading={loading}
            onClick={() => handleGrade(true)}
          >
            정답 처리
          </Button>
          <Button
            danger
            size="large"
            icon={<CloseOutlined />}
            loading={loading}
            onClick={() => handleGrade(false)}
          >
            오답 처리
          </Button>
          <Button size="large" onClick={onClose} disabled={loading}>
            취소
          </Button>
        </Space>
      </div>
    </Modal>
  );
};
